import React, { useState } from 'react';

function PhotoGallery({ photos }) {
  const [selectedIndex, setSelectedIndex] = useState(null);

  // Photo field labels for display (covers both general and damage-specific columns)
  const photoLabels = {
    sticker: 'Sticker',
    stationAndPlinth: 'Station and Plinth',
    loop1: 'Loop 1',
    loop2: 'Loop 2',
    loop3: 'Loop 3',
    loop4: 'Loop 4',
    loop5: 'Loop 5',
    loop6: 'Loop 6',
    Lane: 'Lane',
    Lane_320: 'Lane 320',
    layout: 'Layout',
    layoutOfAllLeftLanes: 'Layout of All Left Lanes',
    // Damage-specific columns
    sticker_a81: 'Sticker (Damage)',
    stationAndPlinth_b24: 'Station & Plinth (Damage)',
    loop11: 'Loop 11',
    loop12: 'Loop 12',
    loop13: 'Loop 13',
    loop14: 'Loop 14',
    loop15: 'Loop 15',
    loop16: 'Loop 16',
    lane2: 'Lane 2',
    lane2_ed7: 'Lane 2 (ed7)',
    general: 'General',
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d)) return null;
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  };

  // Process photos - combine all photos from all records
  const processPhotos = () => {
    const allPhotos = [];

    photos.forEach(record => {
      const date = formatDate(record.StartTime);
      Object.keys(photoLabels).forEach(key => {
        if (record[key] && record[key].trim() !== '') {
          allPhotos.push({
            url: record[key],
            label: photoLabels[key],
            date
          });
        }
      });
    });

    return allPhotos;
  };

  const photoList = processPhotos();

  if (photoList.length === 0) {
    return (
      <div className="text-center text-gray-500 py-4">
        <p className="text-sm">No photos available for this station</p>
      </div>
    );
  }

  return (
    <div>
      {/* Photo Grid - Three Columns */}
      <div className="grid grid-cols-3 gap-4">
        {photoList.map((photo, index) => (
          <div
            key={index}
            className="cursor-pointer group"
            onClick={() => setSelectedIndex(index)}
          >
            <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-200 hover:border-blue-500 transition-all hover:shadow-lg">
              <img
                src={photo.url}
                alt={photo.label}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f0f0f0" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="14"%3ENo Image%3C/text%3E%3C/svg%3E';
                }}
              />
            </div>
            <p className="text-xs text-gray-600 mt-2 text-center">{photo.label}</p>
            {photo.date && (
              <p className="text-[10px] text-gray-400 text-center">{photo.date}</p>
            )}
          </div>
        ))}
      </div>

      {/* Modal for Enlarged Photo with navigation */}
      {selectedIndex !== null && photoList[selectedIndex] && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999] p-4"
          onClick={() => setSelectedIndex(null)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft' && selectedIndex > 0) setSelectedIndex(selectedIndex - 1);
            if (e.key === 'ArrowRight' && selectedIndex < photoList.length - 1) setSelectedIndex(selectedIndex + 1);
            if (e.key === 'Escape') setSelectedIndex(null);
          }}
          tabIndex={0}
          ref={(el) => el && el.focus()}
        >
          {/* Previous Button */}
          {selectedIndex > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); setSelectedIndex(selectedIndex - 1); }}
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-800 rounded-full w-10 h-10 flex items-center justify-center shadow-lg z-20 transition-colors"
              aria-label="Previous photo"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Next Button */}
          {selectedIndex < photoList.length - 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setSelectedIndex(selectedIndex + 1); }}
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-800 rounded-full w-10 h-10 flex items-center justify-center shadow-lg z-20 transition-colors"
              aria-label="Next photo"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          <div
            className="relative max-w-6xl max-h-full bg-white rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setSelectedIndex(null)}
              className="absolute top-4 right-4 bg-white text-gray-800 rounded-full w-10 h-10 flex items-center justify-center hover:bg-gray-200 transition-colors shadow-lg z-10"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Photo Label & Counter */}
            <div className="absolute top-4 left-4 bg-white bg-opacity-90 px-4 py-2 rounded shadow-lg z-10">
              <p className="font-semibold text-gray-800">{photoList[selectedIndex].label}</p>
              <p className="text-xs text-gray-500">
                {selectedIndex + 1} of {photoList.length}
                {photoList[selectedIndex].date && ` \u2022 ${photoList[selectedIndex].date}`}
              </p>
            </div>

            {/* Large Photo */}
            <img
              src={photoList[selectedIndex].url}
              alt={photoList[selectedIndex].label}
              className="max-h-[90vh] w-auto mx-auto"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f0f0f0" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="14"%3ENo Image%3C/text%3E%3C/svg%3E';
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default PhotoGallery;
